import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { readStageConfig, type PackageSizePrices, type WorkshopModule, type WorkshopStageConfig } from '@mustauto/types';
import { api } from '@/lib/api';

// ---------- Labor Prices ----------

export interface LaborPrice {
  id: string;
  serviceName: string;
  basePrice: number;
  modifierNational: number;
  modifierImported: number;
  modifierLuxury: number;
  /**
   * `'estetica'` = pacote de estética (Fase 2); nulo = serviço de mão de obra.
   * Opcionais: uma resposta anterior aos pacotes não traz os campos.
   */
  module?: string | null;
  description?: string | null;
  durationHours?: number | null;
  warrantyMonths?: number | null;
  includedItems?: string[];
  /** Em reais, por porte. Porte ausente = `basePrice`. */
  sizePrices?: PackageSizePrices;
  /** Preço sugerido pelo catálogo padrão, esperando o gestor confirmar. */
  pendingConfirmation?: boolean;
  /** Receita de insumos (F2.6): o que baixa do estoque quando a OS conclui. */
  consumables?: PackageConsumableRow[];
  createdAt: string;
  updatedAt: string;
}

export interface PackageConsumableRow {
  inventoryItemId: string;
  quantity: number;
  /** Quanto gasta em cada porte, quando difere da base. */
  sizeQuantities?: PackageSizePrices;
  name: string;
  unit: string | null;
}

/** Campos de pacote que o POST/PATCH aceitam — ausente não mexe, `null` apaga. */
export interface LaborPackageFields {
  module?: 'estetica' | null;
  description?: string | null;
  durationHours?: number | null;
  warrantyMonths?: number | null;
  includedItems?: string[];
  sizePrices?: PackageSizePrices | null;
  /** Receita inteira: a lista enviada substitui a anterior. */
  consumables?: { inventoryItemId: string; quantity: number; sizeQuantities?: PackageSizePrices }[];
}

export interface LaborPriceSuggestion {
  id: string;
  laborPriceId: string;
  serviceName: string;
  currentPrice: number;
  suggestedPrice: number;
  reason: string | null;
  basedOnOsCount: number | null;
  status: string;
  createdAt: string;
}

export function useLaborPrices() {
  const queryClient = useQueryClient();

  const pricesQuery = useQuery<{ prices: LaborPrice[] }>({
    queryKey: ['labor-prices'],
    queryFn: async () => {
      // Pede o catálogo: semeia os pacotes da estética (uma vez) e traz os de
      // preço a confirmar — o painel é quem mostra e confirma.
      const res = await api.get('/settings/labor-prices?withPackageCatalog=true');
      return res.data;
    },
  });

  const suggestionsQuery = useQuery<{ suggestions: LaborPriceSuggestion[] }>({
    queryKey: ['labor-price-suggestions'],
    queryFn: async () => {
      const res = await api.get('/settings/labor-prices/suggestions');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: {
      serviceName: string;
      basePrice: number;
      modifierNational?: number;
      modifierImported?: number;
      modifierLuxury?: number;
    } & LaborPackageFields) => {
      const res = await api.post('/settings/labor-prices', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labor-prices'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      serviceName?: string;
      basePrice?: number;
      modifierNational?: number;
      modifierImported?: number;
      modifierLuxury?: number;
    } & LaborPackageFields) => {
      const res = await api.patch(`/settings/labor-prices/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labor-prices'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/settings/labor-prices/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labor-prices'] });
      queryClient.invalidateQueries({ queryKey: ['labor-price-suggestions'] });
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/settings/labor-prices/suggestions/${id}/approve`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labor-prices'] });
      queryClient.invalidateQueries({ queryKey: ['labor-price-suggestions'] });
    },
  });

  const confirmPackagesMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/settings/labor-prices/packages/confirm');
      return res.data as { confirmed: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labor-prices'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/settings/labor-prices/suggestions/${id}/reject`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labor-price-suggestions'] });
    },
  });

  return {
    prices: pricesQuery.data?.prices ?? [],
    isLoading: pricesQuery.isLoading,
    isError: pricesQuery.isError,
    error: pricesQuery.error,
    refetch: pricesQuery.refetch,
    suggestions: suggestionsQuery.data?.suggestions ?? [],
    suggestionsLoading: suggestionsQuery.isLoading,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    remove: removeMutation.mutateAsync,
    approve: approveMutation.mutateAsync,
    reject: rejectMutation.mutateAsync,
    confirmPackages: confirmPackagesMutation.mutateAsync,
    isConfirmingPackages: confirmPackagesMutation.isPending,
    isCreating: createMutation.isPending,
  };
}

export function useGenerateLaborSuggestions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await api.post('/settings/labor-prices/suggestions/generate');
      return res.data as { generated: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labor-price-suggestions'] });
    },
  });
}

// ---------- Workshop Profile ----------

export interface WorkshopAddress {
  zip: string | null;
  street: string | null;
  number: string | null;
  complement: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
}

export interface WorkshopProfile {
  id: string;
  name: string;
  legalName: string | null;
  cnpj: string;
  isDemo?: boolean;
  demoResetAt?: string | null;
  /** Há dado de demonstração nesta oficina para limpar (flag ligada ou seed remanescente). */
  hasDemoData?: boolean;
  address: WorkshopAddress | null;
  settings: {
    address?: string;
    phone?: string;
    hours?: { day: number; open: string; close: string; closed: boolean }[];
    specialties?: string[];
    photos?: string[];
    slots?: { name: string; type: 'bay' | 'lift'; active: boolean }[];
    onboarding_completed?: boolean;
    onboarding_step?: number;
    /** Ausente = listada (default histórico). */
    listedInSearch?: boolean;
  };
  /**
   * Limiar de "OS parada", já resolvido pelo servidor.
   *
   * Opcional porque o painel e a API sobem em deploys separados: se o web
   * chegar primeiro, a resposta ainda não traz o campo. A tela deriva o valor
   * localmente nesse caso, com a mesma regra (`@mustauto/utils`).
   */
  staleOs?: {
    hours: number;
    suggested: number;
    isOverride: boolean;
    choices: number[];
  };
}

export function useWorkshopProfile() {
  const queryClient = useQueryClient();

  const profileQuery = useQuery<WorkshopProfile>({
    queryKey: ['workshop-profile'],
    queryFn: async () => {
      const res = await api.get('/settings/profile');
      return res.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (
      data: Partial<WorkshopProfile['settings']> & {
        name?: string;
        legalName?: string;
        cnpj?: string;
        addressZip?: string;
        addressStreet?: string;
        addressNumber?: string;
        addressComplement?: string;
        addressNeighborhood?: string;
        addressCity?: string;
        addressState?: string;
        /** `null` devolve a oficina ao limiar derivado do segmento dela. */
        staleOsHours?: number | null;
      },
    ) => {
      const res = await api.patch('/settings/profile', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workshop-profile'] });
      // Os módulos ligados e o padrão da abertura são derivados das
      // especialidades — salvar o perfil pode mudar os dois.
      queryClient.invalidateQueries({ queryKey: WORKSHOP_MODULES_KEY });
    },
  });

  const presignMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/settings/profile/presign');
      return res.data as { uploadUrl: string; s3Key: string };
    },
  });

  return {
    profile: profileQuery.data ?? null,
    isLoading: profileQuery.isLoading,
    updateProfile: updateMutation.mutateAsync,
    getPresignedUrl: presignMutation.mutateAsync,
    isSaving: updateMutation.isPending,
  };
}

// ---------- Módulos (tipos de serviço) ----------

/**
 * Módulos verticais da oficina (mecânica, funilaria, estética) e o padrão da
 * abertura de OS — `GET /settings/modules`, o mesmo contrato do app.
 *
 * Mecânica é sempre ligada (`locked`). Os demais nascem das especialidades
 * (`declared`) e o gestor pode contrariá-las (`choice`); `choice: null` é
 * "seguindo as especialidades".
 */
export interface WorkshopModuleRow {
  module: WorkshopModule;
  label: string;
  enabled: boolean;
  declared: boolean;
  locked: boolean;
  choice: boolean | null;
}

export interface WorkshopModulesPayload {
  modules: WorkshopModuleRow[];
  defaultModule: { module: WorkshopModule; suggested: WorkshopModule; isExplicit: boolean };
  /**
   * As etapas de cada módulo, na ordem da oficina (F3.8). Opcional: a API
   * anterior à F3.8 não manda, e aí vale a lista padrão.
   */
  stages?: Record<string, { key: string; label: string; hidden: boolean; photo: boolean; position: number | null }[]>;
}

export const WORKSHOP_MODULES_KEY = ['workshop-modules'] as const;

async function fetchWorkshopModules(): Promise<WorkshopModulesPayload> {
  const res = await api.get('/settings/modules');
  return res.data;
}

/**
 * As etapas DESTA oficina (F3.8) — nome, ordem, escondidas, criadas e foto —,
 * para passar às funções de etapa de `@mustauto/types` (`getStageLabel`,
 * `moduleStages`, `stageRequiresPhoto`…). Lê o mesmo `GET /settings/modules`
 * do `useWorkshopModules`, pelo mesmo cache.
 *
 * Enquanto carrega (ou se falhar), devolve `{}` — e `{}` é a lista padrão: a
 * tela mostra os nomes de sempre em vez de travar à espera da configuração.
 */
export function useStageConfig(): WorkshopStageConfig {
  const query = useQuery<WorkshopModulesPayload>({
    queryKey: WORKSHOP_MODULES_KEY,
    queryFn: fetchWorkshopModules,
    staleTime: 5 * 60_000,
  });
  const stages = query.data?.stages;
  return useMemo(() => readStageConfig(stages), [stages]);
}

export function useWorkshopModules() {
  const queryClient = useQueryClient();

  const query = useQuery<WorkshopModulesPayload>({
    queryKey: WORKSHOP_MODULES_KEY,
    queryFn: fetchWorkshopModules,
  });

  // A resposta dos dois PUTs já é o estado inteiro — vira o cache direto, sem
  // um GET a mais. Também no erro relê: um 400 aqui costuma ser outro gestor
  // desligando o módulo ao mesmo tempo, e a tela precisa mostrar o real.
  const onSettled = (data: WorkshopModulesPayload | undefined) => {
    if (data) queryClient.setQueryData(WORKSHOP_MODULES_KEY, data);
    else void queryClient.invalidateQueries({ queryKey: WORKSHOP_MODULES_KEY });
    // O card de etapas lista só os tipos ligados.
    void queryClient.invalidateQueries({ queryKey: ['settings', 'stages'] });
    // Ligar a Estética semeia o catálogo de pacotes na próxima leitura.
    void queryClient.invalidateQueries({ queryKey: ['labor-prices'] });
  };

  const enabledMutation = useMutation({
    mutationFn: async (data: { module: WorkshopModule; enabled: boolean }) => {
      const res = await api.put('/settings/modules', data);
      return res.data as WorkshopModulesPayload;
    },
    onSettled,
  });

  const defaultMutation = useMutation({
    mutationFn: async (module: WorkshopModule) => {
      const res = await api.put('/settings/modules/default', { module });
      return res.data as WorkshopModulesPayload;
    },
    onSettled,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    setEnabled: enabledMutation.mutateAsync,
    setDefault: defaultMutation.mutateAsync,
    /** O módulo sendo salvo agora (`'default'` para o padrão), ou `null`. */
    saving: enabledMutation.isPending
      ? (enabledMutation.variables?.module ?? null)
      : defaultMutation.isPending
        ? 'default'
        : null,
  };
}

// ---------- Demo Data Cleanup ----------

/**
 * Remove o seed de demonstração da oficina. Só existe o lado da SAÍDA: a
 * demonstração não é mais oferecida dentro da conta do cliente (ela vive na
 * oficina compartilhada que o visitante anônimo abre pelo login). Isto serve a
 * quem carregou dados fictícios enquanto a opção existia.
 */
export function useClearDemoData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await api.post('/settings/demo-data/clear');
      return res.data as { ok: true; cleared: boolean };
    },
    onSuccess: () => {
      // A limpeza mexe em OS, veículos, estoque e preços — a tela inteira ficou
      // velha, não só o perfil.
      queryClient.invalidateQueries();
    },
  });
}

// ── Etapas da oficina (F3.8) ──

export interface WorkshopStageRow {
  key: string;
  label: string;
  /** Nome padrão ("Cabine de pintura"); `null` na etapa criada pela oficina. */
  defaultLabel: string | null;
  hidden: boolean;
  photo: boolean;
  position: number | null;
  isDefault: boolean;
  isPaintBooth: boolean;
  /** OS na etapa agora. */
  openOrders: number;
  /** Tempo configurado (F3.7), em horas, ou `null`. */
  hours: number | null;
}

export interface WorkshopStagesPayload {
  limits: { maxStages: number; maxLabelLength: number };
  durationChoices: number[];
  modules: {
    module: string;
    label: string;
    customized: boolean;
    stages: WorkshopStageRow[];
    defaults: { key: string; label: string; photo: boolean }[];
  }[];
}

/** Etapa como a tela manda no PUT: a nova vem sem `key`. */
export interface StageInput {
  key?: string;
  label: string;
  hidden?: boolean;
  photo?: boolean;
}

const WORKSHOP_STAGES_KEY = ['settings', 'stages'] as const;

/**
 * Configurações → Etapas (F3.8): `GET/PUT /settings/stages`. Os tempos (F3.7)
 * continuam salvos por `PUT /settings/stage-durations` — a tela junta os dois
 * num "Salvar" só.
 */
export function useWorkshopStages() {
  const queryClient = useQueryClient();

  const query = useQuery<WorkshopStagesPayload>({
    queryKey: WORKSHOP_STAGES_KEY,
    queryFn: async () => (await api.get('/settings/stages')).data,
  });

  // Nome, ordem e escondidas mudam a OS inteira do painel: o seletor, a lista,
  // o histórico, a cabine e a previsão. Tudo relê.
  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: WORKSHOP_STAGES_KEY });
    void queryClient.invalidateQueries({ queryKey: WORKSHOP_MODULES_KEY });
    void queryClient.invalidateQueries({ queryKey: ['settings', 'stage-durations'] });
    void queryClient.invalidateQueries({ queryKey: ['service-order'] });
    void queryClient.invalidateQueries({ queryKey: ['service-orders'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['paint-booth'] });
  };

  const saveStages = useMutation({
    mutationFn: async (data: { module: string; stages: StageInput[] }) =>
      (await api.put('/settings/stages', data)).data as WorkshopStagesPayload,
    onSuccess: (data) => queryClient.setQueryData(WORKSHOP_STAGES_KEY, data),
    onSettled: invalidateAll,
  });

  const saveDurations = useMutation({
    mutationFn: async (data: { module: string; durations: Record<string, number>; complete?: boolean }) =>
      (await api.put('/settings/stage-durations', data)).data,
    onSettled: invalidateAll,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    saveStages: saveStages.mutateAsync,
    saveDurations: saveDurations.mutateAsync,
  };
}
